import React, { useState, useEffect } from 'react';
import UnifiedInquirySection from '../../components/features/UnifiedInquirySection';
import { useNavigate, useParams } from 'react-router-dom';
import './UseCases.css';
import UseCaseModal from '../../components/features/UseCaseModal';
import InquiryModal from '../../components/features/InquiryModal';

import { getCategories, getCases } from '../../api/cases';
import { getActiveQuestionnaires } from '../../auto-estimate/services/estimateApi';

const UseCases = () => {
    const navigate = useNavigate();
    const [activeCategory, setActiveCategory] = useState({ id: 'ALL', code: 'ALL', label: '전체' });
    const [categories, setCategories] = useState([]);
    const [cases, setCases] = useState([]);
    const [estimateVersions, setEstimateVersions] = useState([]); // Estimate versions for card display
    const [selectedCase, setSelectedCase] = useState(null);
    // eslint-disable-next-line no-unused-vars
    const [loading, setLoading] = useState(true);

    const { id } = useParams();

    // Sync Modal state with URL Route
    useEffect(() => {
        if (id && cases.length > 0) {
            const targetCase = cases.find(c => c.id === parseInt(id, 10)); // cases ID is highly likely integer/string
            // Or if UUID, c.id === id. Let's make it robust by checking both:
            const foundCase = cases.find(c => String(c.id) === String(id));
            if (foundCase) {
                setSelectedCase(foundCase);
            }
        } else if (!id && selectedCase) {
            setSelectedCase(null);
        }
    }, [id, cases]);

    // Browser tab title: detail-modal path (/use-cases/:id) is skipped by the
    // Layout fallback, so we own the title here. List view returns to the
    // Layout-managed default on case close.
    useEffect(() => {
        if (selectedCase?.title) {
            document.title = `${selectedCase.title} | 구축 사례 | 가까운솔루션`;
        } else if (!id) {
            document.title = '구축 사례 | 가까운솔루션';
        }
    }, [selectedCase, id]);

    // Inquiry Modal State
    const [inquiryModal, setInquiryModal] = useState({ isOpen: false, type: 'CONTACT' });

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [catsData, casesData, versionsData] = await Promise.all([
                    getCategories(),
                    getCases(),
                    getActiveQuestionnaires()
                ]);
                // Transform categories to match UI needs if necessary, though backend returns compatible structure
                // Backend returns [{ id, code, label, order }, ...]
                setCategories([{ id: 'ALL', code: 'ALL', label: '전체' }, ...catsData]);
                setCases(casesData);
                setEstimateVersions(versionsData || []);
            } catch (err) {
                console.error("Failed to load use cases:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    const filteredCases = activeCategory.code === 'ALL'
        ? cases
        : cases.filter(c => c.category && c.category.code === activeCategory.code);

    // eslint-disable-next-line no-unused-vars
    const openInquiryModal = (type) => {
        setInquiryModal({ isOpen: true, type });
    };

    return (
        <div className="use-cases-page">
            <InquiryModal
                isOpen={inquiryModal.isOpen}
                onClose={() => setInquiryModal(prev => ({ ...prev, isOpen: false }))}
                type={inquiryModal.type}
            />

            <div className="page-hero">
                <div className="container">
                    <h1 className="page-title">고객 적용 사례</h1>
                    <p className="page-subtitle">
                        가까운솔루션은 <strong>인터넷 연결이 없는 환경</strong>에서도 완벽하게 작동합니다.<br />
                        다양한 현장에서 검증된 효율을 확인해보세요.
                    </p>
                </div>
            </div>

            <div className="container">
                {/* Category Tabs */}
                <div className="category-tabs">
                    {categories.map(cat => (
                        <button
                            key={cat.id}
                            className={`cat-btn ${activeCategory.id === cat.id ? 'active' : ''}`}
                            onClick={() => setActiveCategory(cat)}
                        >
                            {cat.label}
                        </button>
                    ))}
                </div>

                {/* CSS for Tabs (Inline for now, usually in CSS file) */}
                <style>{`
          .category-tabs {
            display: flex;
            flex-wrap: wrap;
            gap: 0.5rem;
            justify-content: center;
            margin-bottom: 3rem;
          }
          .cat-btn {
            padding: 0.6rem 1.2rem;
            border: 1px solid var(--border-color);
            background: white;
            border-radius: 20px;
            cursor: pointer;
            font-size: 0.95rem;
            transition: all 0.2s;
            color: var(--text-light);
          }
          .cat-btn:hover {
            border-color: var(--primary-color);
            color: var(--primary-color);
          }
          .cat-btn.active {
            background-color: var(--primary-color);
            color: white;
            border-color: var(--primary-color);
            font-weight: bold;
          }
        `}</style>

                <div className="category-description" style={{ textAlign: 'center', marginBottom: '2rem', maxWidth: '800px', margin: '0 auto 2rem auto', color: '#666', lineHeight: '1.6' }}>
                    {activeCategory.code !== 'ALL' && activeCategory.description && (
                        <p>{activeCategory.description}</p>
                    )}

                    {activeCategory.code !== 'ALL' && activeCategory.relatedVersionId && (() => {
                        const matchedVersion = estimateVersions.find(v => v.id === activeCategory.relatedVersionId);
                        if (!matchedVersion) return null;
                        return (
                            <div
                                className="estimate-shortcut-card"
                                onClick={() => navigate('/estimate', { state: { versionId: activeCategory.relatedVersionId } })}
                                style={{
                                    marginTop: '1.5rem',
                                    padding: '1rem 1.5rem',
                                    background: 'linear-gradient(135deg, #fff 0%, #f8fafc 100%)',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '12px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '1rem',
                                    transition: 'all 0.2s ease',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                                }}
                                onMouseOver={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-2px)';
                                    e.currentTarget.style.boxShadow = '0 8px 20px rgba(225, 29, 72, 0.15)';
                                    e.currentTarget.style.borderColor = 'var(--primary-color)';
                                }}
                                onMouseOut={(e) => {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.04)';
                                    e.currentTarget.style.borderColor = '#e2e8f0';
                                }}
                            >
                                <div style={{ fontSize: '2rem' }}>📋</div>
                                <div style={{ flex: 1, textAlign: 'left' }}>
                                    <div style={{ fontWeight: '700', fontSize: '1.1rem', color: '#222', marginBottom: '4px' }}>
                                        {matchedVersion.label || matchedVersion.name}
                                    </div>
                                    {matchedVersion.description && (
                                        <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '4px' }}>
                                            {matchedVersion.description}
                                        </div>
                                    )}
                                    {matchedVersion.totalQuestions > 0 && (
                                        <div style={{ fontSize: '0.85rem', color: '#888' }}>
                                            ⏱️ 약 {matchedVersion.estimatedMinutesMin === matchedVersion.estimatedMinutesMax
                                                ? `${matchedVersion.estimatedMinutesMin}분`
                                                : `${matchedVersion.estimatedMinutesMin}~${matchedVersion.estimatedMinutesMax}분`}
                                            {' '}
                                            ({matchedVersion.minQuestions === matchedVersion.totalQuestions
                                                ? `${matchedVersion.totalQuestions}문항`
                                                : `${matchedVersion.minQuestions}~${matchedVersion.totalQuestions}문항`})
                                        </div>
                                    )}
                                </div>
                                <div style={{
                                    padding: '8px 16px',
                                    background: 'var(--primary-color)',
                                    color: 'white',
                                    borderRadius: '8px',
                                    fontWeight: '600',
                                    fontSize: '0.9rem',
                                    whiteSpace: 'nowrap'
                                }}>
                                    견적 확인 →
                                </div>
                            </div>
                        );
                    })()}
                </div>

                <div className="cases-list">
                    {filteredCases.map((item) => (
                        <div key={item.id} className="case-card" onClick={() => navigate(`/use-cases/${item.id}`)} style={{ cursor: 'pointer' }}>
                            {/* Thumbnail Image */}
                            {item.images && item.images.length > 0 && (
                                <div
                                    className="case-thumbnail"
                                    style={{ backgroundImage: `url(${item.images[0]})` }}
                                />
                            )}

                            <div className="case-content-wrapper">
                                <div className="case-header">
                                    <span className="case-icon">{item.icon}</span>
                                    <div>
                                        <span style={{ fontSize: '0.8rem', color: '#888', display: 'block' }}>{item.category?.label || item.category}</span>
                                        <h3 className="case-title" style={{ margin: 0, fontSize: '1.2rem' }}>{item.title}</h3>
                                    </div>
                                </div>
                                <div className="case-body" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                                    <p style={{ height: '4.5rem', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', marginBottom: '1rem', color: '#666' }}>
                                        {item.description}
                                    </p>
                                    <div style={{ marginTop: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem' }}>
                                        {item.category?.relatedVersionId ? (
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    navigate('/estimate', { state: { versionId: item.category.relatedVersionId } });
                                                }}
                                                title="이 카테고리의 3분 셀프견적으로 바로 이동"
                                                style={{
                                                    display: 'inline-flex', alignItems: 'center', gap: '4px',
                                                    background: 'var(--primary-color)', color: 'white',
                                                    border: 'none', borderRadius: '999px',
                                                    padding: '4px 10px', fontSize: '0.78rem', fontWeight: 700,
                                                    cursor: 'pointer',
                                                    boxShadow: '0 2px 8px rgba(255,51,102,0.28)',
                                                    lineHeight: 1.1, whiteSpace: 'nowrap'
                                                }}
                                            >
                                                🚀 3분견적
                                            </button>
                                        ) : <span />}
                                        <span style={{ color: 'var(--primary-color)', fontSize: '0.9rem', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                                            자세히 보기 &rarr;
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                <div style={{ marginTop: '3rem' }}>
                    <UnifiedInquirySection title="비슷한 환경이신가요? 우리 회사 맞춤 견적을 받아보세요." buttonText="📐 맞춤 견적 요청하기" buttonAction="ESTIMATE" />
                </div>
            </div>

            <UseCaseModal isOpen={!!selectedCase} onClose={() => navigate('/use-cases')} data={selectedCase} />
        </div>
    );
};

export default UseCases;
